import {useRef, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';

const {FiArrowUpRight,FiAlertCircle,FiPhone,FiMail} = FiIcons;
const types = ['Driveway', 'Patio', 'Pool deck', 'Walkway', 'Not sure yet'];
const timelines = ['As soon as possible', 'Within 3 months', 'Within 6 months', 'Just exploring'];
const initialForm = {name: '', email: '', phone: '', type: '', timeline: '', details: ''};

function ContactMethod({href, icon, label, value, note}) {
  return (
    <a href={href} className="group flex items-center justify-between gap-5 border-b border-ink/15 py-6 transition-colors hover:border-brick">
      <span className="flex items-center gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center bg-sand text-xl text-brick transition-colors group-hover:bg-brick group-hover:text-white">
          <SafeIcon icon={icon} />
        </span>
        <span className="min-w-0">
          <span className="eyebrow block">{label}</span>
          <span className="mt-1 block break-all text-base font-bold sm:text-xl">{value}</span>
          <span className="mt-1 block text-xs text-stone">{note}</span>
        </span>
      </span>
      <SafeIcon icon={FiArrowUpRight} className="shrink-0 text-xl text-brick transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
    </a>
  );
}

function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('');
  const [sendError, setSendError] = useState('');
  const [website, setWebsite] = useState('');
  const submissionId = useRef(null);
  const set = (key) => (event) => {
    setForm((current) => ({...current, [key]: event.target.value}));
    setStatus(''); setSendError(''); submissionId.current = null;
  };

  async function submit(event) {
    event.preventDefault();
    if (status === 'sending') return;
    const next = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'Please enter a valid email.';
    if (!form.type) next.type = 'Choose the space you are planning.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus('sending');
    setSendError('');
    if (!submissionId.current) submissionId.current = crypto.randomUUID();
    try {
      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({...form, website, submissionId: submissionId.current})
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.error || 'Your request could not be sent. Please try again or call (954) 695-5814.');
      setStatus('sent');
      setForm(initialForm);
      submissionId.current = null;
    } catch (error) {
      setStatus('error');
      setSendError(error.message || 'Your request could not be sent. Please try again.');
    }
  }

  const error = (key) => errors[key] ? (
    <span className="flex items-center gap-1 text-xs font-medium normal-case tracking-normal text-brick" role="alert">
      <SafeIcon icon={FiAlertCircle} /> {errors[key]}
    </span>
  ) : null;

  return (
    <>
      <PageHero
        eyebrow="Start a conversation"
        title="Your next chapter"
        accent="starts outside."
        text="A beautiful outdoor space begins with a simple hello. Call, email, or tell us what you have in mind."
        image="https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&amp;fit=crop&amp;w=2000&amp;q=85"
      />
      <section className="py-20 md:py-28">
        <div className="wrap grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow">Let's make it happen</p>
            <h2 className="h-display mt-5 text-5xl md:text-7xl">
              Great spaces begin with <em className="text-brick">great conversations.</em>
            </h2>
            <p className="mt-6 max-w-md text-sm leading-8 text-stone">
              Whether you have a complete vision or just a few ideas, we would love to hear about your driveway, patio, pool deck or garden path.
            </p>
            <div className="mt-10 border-t border-ink/15">
              <ContactMethod href="tel:+19546955814" icon={FiPhone} label="Give us a call" value="(954) 695-5814" note="Tap to call directly" />
              <ContactMethod href="mailto:morris@uspaverco.com" icon={FiMail} label="Send an email" value="morris@uspaverco.com" note="Open your email app" />
            </div>
            <div className="mt-12 border-l-2 border-brick pl-5">
              <p className="h-display text-3xl">30+ years of getting it right.</p>
              <p className="mt-2 text-sm leading-7 text-stone">Thoughtful guidance, carefully installed pavers, and a space that feels like yours.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} noValidate className="grid gap-6 bg-white p-7 shadow-[0_30px_80px_rgba(20,17,15,0.1)] md:p-12">
              <div className="border-b border-ink/10 pb-6">
                <p className="eyebrow">Tell us your vision / 01</p>
                <h3 className="h-display mt-3 text-4xl md:text-5xl">Let's talk about <em className="text-brick">your space.</em></h3>
                <p className="mt-3 text-sm leading-6 text-stone">Tell us about your project and send your request directly to our team.</p>
              </div>
              <label className="hidden" aria-hidden="true">
                Website <input name="website" value={website} onChange={(event) => setWebsite(event.target.value)} tabIndex={-1} autoComplete="off" />
              </label>
              <fieldset disabled={status === 'sending'} className="contents">
              <div className="grid gap-6 md:grid-cols-2">
                <label className="grid gap-2 text-xs font-bold uppercase tracking-widest">
                  Name *
                  <input className="field" name="name" maxLength={150} value={form.name} onChange={set('name')} autoComplete="name" aria-invalid={!!errors.name} />
                  {error('name')}
                </label>
                <label className="grid gap-2 text-xs font-bold uppercase tracking-widest">
                  Email *
                  <input className="field" type="email" name="email" maxLength={254} value={form.email} onChange={set('email')} autoComplete="email" aria-invalid={!!errors.email} />
                  {error('email')}
                </label>
              </div>
              <label className="grid gap-2 text-xs font-bold uppercase tracking-widest">
                Phone (optional)
                <input className="field" type="tel" name="phone" maxLength={50} value={form.phone} onChange={set('phone')} autoComplete="tel" />
              </label>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest">Which space? *</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {types.map((type) => (
                    <button key={type} type="button" aria-pressed={form.type === type} onClick={() => { setForm((current) => ({...current, type})); setStatus(''); setSendError(''); submissionId.current = null; }} className={`px-4 py-3 text-xs font-bold transition ${form.type === type ? 'bg-ink text-white' : 'border border-ink/20 hover:border-ink'}`}>
                      {type}
                    </button>
                  ))}
                </div>
                <div className="mt-2">{error('type')}</div>
              </div>
              <label className="grid gap-2 text-xs font-bold uppercase tracking-widest">
                Timeline
                <select className="field" name="timeline" value={form.timeline} onChange={set('timeline')}>
                  <option value="">Select a timeline</option>
                  {timelines.map((timeline) => <option key={timeline} value={timeline}>{timeline}</option>)}
                </select>
              </label>
              <label className="grid gap-2 text-xs font-bold uppercase tracking-widest">
                Tell us more
                <textarea className="field" rows="4" name="details" maxLength={5000} value={form.details} onChange={set('details')} placeholder="Size, style ideas, questions..." />
              </label>
              <button type="submit" className="btn btn-brick w-full">
                {status === 'sending' ? 'Sending…' : 'Send my request'} <SafeIcon icon={FiArrowUpRight} className="text-base" />
              </button>
              </fieldset>
              {status === 'sent' && <p role="status" className="bg-sand p-4 text-sm leading-6">Thank you! Your project request has been sent. Our team will be in touch soon.</p>}
              {sendError && <p role="alert" className="bg-sand p-4 text-sm leading-6">{sendError}</p>}
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export default Contact;
